import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload, FileText, Trash2, Users, UserPlus, LogOut, Loader2,
  GraduationCap, Inbox, ArrowLeft,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SUBJECTS = ["Mathematics", "Science", "Physics (CBSE)", "Physics (AHSEC)"];
const CLASSES = ["Class 9", "Class 10", "Class 11", "Class 12"];
const CATEGORIES = ["Question Papers", "Chapter-wise Handouts"];
const TABS = [
  { id: "notes", label: "Study Notes", icon: FileText },
  { id: "students", label: "Students", icon: Users },
  { id: "enquiries", label: "Enquiries", icon: Inbox },
];

export default function Admin() {
  const { user, loading, logout, promptLogin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("notes");

  const [notes, setNotes] = useState([]);
  const [students, setStudents] = useState([]);
  const [enquiries, setEnquiries] = useState([]);

  // upload form
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [cls, setCls] = useState("");
  const [category, setCategory] = useState("");
  const [uploading, setUploading] = useState(false);

  // student form
  const [sName, setSName] = useState("");
  const [sEmail, setSEmail] = useState("");
  const [sPwd, setSPwd] = useState("");
  const [addingStudent, setAddingStudent] = useState(false);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (loading) return;
    if (!isAdmin) {
      promptLogin((u) => {
        if (u?.role !== "admin") {
          toast.error("Admin access required");
          navigate("/");
        }
      });
    }
  }, [loading, isAdmin, promptLogin, navigate]);

  const loadAll = async () => {
    try {
      const [n, s, e] = await Promise.all([
        api.get("/notes"),
        api.get("/admin/students"),
        api.get("/contacts"),
      ]);
      setNotes(n.data);
      setStudents(s.data);
      setEnquiries(e.data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (isAdmin) loadAll();
  }, [isAdmin]);

  const uploadNote = async (e) => {
    e.preventDefault();
    if (!file || !title || !subject || !cls || !category) {
      toast.error("Fill all fields and choose a PDF.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("subject", subject);
    fd.append("cls", cls);
    fd.append("category", category);
    setUploading(true);
    try {
      await api.post("/admin/notes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Note uploaded");
      setFile(null);
      setTitle("");
      setSubject("");
      setCls("");
      setCategory("");
      document.getElementById("note-file-input").value = "";
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteNote = async (id) => {
    try {
      await api.delete(`/admin/notes/${id}`);
      toast.success("Note removed");
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch {
      toast.error("Could not remove note");
    }
  };

  const addStudent = async (e) => {
    e.preventDefault();
    if (!sName || !sEmail) {
      toast.error("Enter student name and email.");
      return;
    }
    setAddingStudent(true);
    try {
      const payload = { name: sName, email: sEmail };
      if (sPwd) payload.password = sPwd;
      await api.post("/admin/students", payload);
      toast.success("Student added");
      setSName("");
      setSEmail("");
      setSPwd("");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add student");
    } finally {
      setAddingStudent(false);
    }
  };

  const deleteStudent = async (id) => {
    try {
      await api.delete(`/admin/students/${id}`);
      toast.success("Student removed");
      setStudents((prev) => prev.filter((s) => s.id !== id));
    } catch {
      toast.error("Could not remove student");
    }
  };

  const inputCls =
    "w-full bg-white border border-ink/15 rounded-lg px-4 py-3 text-ink placeholder:text-ink/40 focus:border-flame outline-none transition-colors";

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-paper">
        <Loader2 className="h-8 w-8 animate-spin text-flame" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen grid place-items-center bg-paper px-6 text-center">
        <div>
          <GraduationCap className="h-10 w-10 text-flame mx-auto" />
          <p className="mt-4 font-heading font-bold text-xl text-ink">Admin access required</p>
          <p className="mt-2 text-ink/55">Please log in with the admin account to continue.</p>
          <div className="mt-6 flex gap-3 justify-center">
            <button onClick={() => promptLogin()} data-testid="admin-login-prompt" className="bg-flame text-white font-semibold px-6 py-3 rounded-full hover:bg-ink transition-colors">Log In</button>
            <button onClick={() => navigate("/")} className="border border-ink/20 text-ink font-semibold px-6 py-3 rounded-full hover:border-flame transition-colors">Back to site</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-ink text-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 bg-flame rounded-md font-heading font-extrabold text-sm">NK</span>
            <span className="font-heading font-bold tracking-tight">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="hidden sm:inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm transition-colors">
              <ArrowLeft className="h-4 w-4" /> View site
            </button>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 bg-white/10 hover:bg-flame text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                  tab === t.id ? "bg-ink text-white" : "bg-white border border-ink/10 text-ink/70 hover:border-flame"
                }`}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* NOTES */}
        {tab === "notes" && (
          <div className="grid lg:grid-cols-12 gap-8">
            <form onSubmit={uploadNote} data-testid="upload-form" className="lg:col-span-5 bg-white border border-ink/10 rounded-2xl p-6 h-fit">
              <h3 className="font-heading font-bold text-xl text-ink flex items-center gap-2">
                <Upload className="h-5 w-5 text-flame" /> Upload PDF Note
              </h3>
              <div className="mt-5 space-y-4">
                <div>
                  <label className="text-sm font-medium text-ink/70">Title</label>
                  <input data-testid="note-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Motion — Chapter 1" className={`mt-1.5 ${inputCls}`} />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/70">Subject</label>
                  <Select value={subject} onValueChange={setSubject}>
                    <SelectTrigger data-testid="note-subject" className="mt-1.5 bg-white border-ink/15 h-[46px]"><SelectValue placeholder="Select subject" /></SelectTrigger>
                    <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/70">Class</label>
                  <Select value={cls} onValueChange={setCls}>
                    <SelectTrigger data-testid="note-class" className="mt-1.5 bg-white border-ink/15 h-[46px]"><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/70">Category</label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="note-category" className="mt-1.5 bg-white border-ink/15 h-[46px]"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/70">PDF File</label>
                  <input id="note-file-input" data-testid="note-file" type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1.5 block w-full text-sm text-ink/70 file:mr-4 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:bg-ink file:text-white file:font-semibold hover:file:bg-flame file:cursor-pointer" />
                </div>
                <button type="submit" disabled={uploading} data-testid="upload-submit" className="w-full inline-flex items-center justify-center gap-2 bg-flame text-white font-semibold px-6 py-3.5 rounded-full hover:bg-ink transition-colors disabled:opacity-60">
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploading ? "Uploading..." : "Upload Note"}
                </button>
              </div>
            </form>

            <div className="lg:col-span-7">
              <h3 className="font-heading font-bold text-xl text-ink mb-4">Uploaded Notes ({notes.length})</h3>
              {notes.length === 0 ? (
                <p className="text-ink/50 border border-dashed border-ink/20 rounded-2xl p-8 text-center">No notes uploaded yet.</p>
              ) : (
                <div className="space-y-3">
                  {notes.map((n) => (
                    <div key={n.id} data-testid={`admin-note-${n.id}`} className="flex items-center gap-4 bg-white border border-ink/10 rounded-xl p-4">
                      <FileText className="h-6 w-6 text-flame shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-ink truncate">{n.title}</div>
                        <div className="text-xs text-ink/50">{n.subject} · {n.cls} · {n.category || "Chapter-wise Handouts"}</div>
                      </div>
                      <button onClick={() => deleteNote(n.id)} data-testid={`delete-note-${n.id}`} className="grid place-items-center h-9 w-9 rounded-lg text-ink/50 hover:text-white hover:bg-red-500 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* STUDENTS */}
        {tab === "students" && (
          <div className="grid lg:grid-cols-12 gap-8">
            <form onSubmit={addStudent} data-testid="student-form" className="lg:col-span-5 bg-white border border-ink/10 rounded-2xl p-6 h-fit">
              <h3 className="font-heading font-bold text-xl text-ink flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-flame" /> Add Student
              </h3>
              <div className="mt-5 space-y-4">
                <div>
                  <label className="text-sm font-medium text-ink/70">Name</label>
                  <input data-testid="student-name" value={sName} onChange={(e) => setSName(e.target.value)} placeholder="Student full name" className={`mt-1.5 ${inputCls}`} />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/70">Email (login ID)</label>
                  <input data-testid="student-email" type="email" value={sEmail} onChange={(e) => setSEmail(e.target.value)} placeholder="student@example.com" className={`mt-1.5 ${inputCls}`} />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/70">Password</label>
                  <input data-testid="student-password" value={sPwd} onChange={(e) => setSPwd(e.target.value)} placeholder="Leave blank for default (Nkscphy@)" className={`mt-1.5 ${inputCls}`} />
                  <p className="mt-1 text-xs text-ink/40">If left blank, the default password Nkscphy@ is used.</p>
                </div>
                <button type="submit" disabled={addingStudent} data-testid="student-submit" className="w-full inline-flex items-center justify-center gap-2 bg-flame text-white font-semibold px-6 py-3.5 rounded-full hover:bg-ink transition-colors disabled:opacity-60">
                  {addingStudent ? <Loader2 className="h-5 w-5 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                  {addingStudent ? "Adding..." : "Add Student"}
                </button>
              </div>
            </form>

            <div className="lg:col-span-7">
              <h3 className="font-heading font-bold text-xl text-ink mb-4">Registered Students ({students.length})</h3>
              {students.length === 0 ? (
                <p className="text-ink/50 border border-dashed border-ink/20 rounded-2xl p-8 text-center">No students added yet.</p>
              ) : (
                <div className="space-y-3">
                  {students.map((s) => (
                    <div key={s.id} data-testid={`admin-student-${s.id}`} className="flex items-center gap-4 bg-white border border-ink/10 rounded-xl p-4">
                      <span className="grid place-items-center h-9 w-9 rounded-full bg-ink text-white font-heading font-bold shrink-0">{(s.name || s.email)[0]?.toUpperCase()}</span>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-ink truncate">{s.name}</div>
                        <div className="text-xs text-ink/50 truncate">{s.email}</div>
                      </div>
                      <button onClick={() => deleteStudent(s.id)} data-testid={`delete-student-${s.id}`} className="grid place-items-center h-9 w-9 rounded-lg text-ink/50 hover:text-white hover:bg-red-500 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ENQUIRIES */}
        {tab === "enquiries" && (
          <div>
            <h3 className="font-heading font-bold text-xl text-ink mb-4">Contact Enquiries ({enquiries.length})</h3>
            {enquiries.length === 0 ? (
              <p className="text-ink/50 border border-dashed border-ink/20 rounded-2xl p-8 text-center">No enquiries yet.</p>
            ) : (
              <div className="space-y-3">
                {enquiries.map((c) => (
                  <div key={c.id} data-testid={`admin-enquiry-${c.id}`} className="bg-white border border-ink/10 rounded-xl p-5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="font-semibold text-ink">{c.name} <span className="text-ink/40 font-normal">· {c.student_class} · {c.subject}</span></div>
                      <a href={`tel:${c.phone}`} className="text-flame font-semibold text-sm">{c.phone}</a>
                    </div>
                    <p className="mt-2 text-ink/70 text-sm">{c.message}</p>
                    <div className="mt-2 text-xs text-ink/40">{new Date(c.created_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
